/** @jsx jsx */
import { Hr, Spacer } from "@cd2/components/build/es6"
import { P } from "@cd2/text/build/es6"
import { humanize, titleize } from "@cd2/tools/build/es6"
import { css, jsx } from "@emotion/core"
import { endOfMonth, startOfMonth, sub } from "date-fns"
import { startOfYear } from "date-fns/esm"
import React from "react"
import { brandColors, colors } from "../Components/EOSThemeProvider"
import { EventList } from "../Components/EventsList"
import { ExternalLink } from "../Components/ExternalLink"
import { useGlobalState } from "../Components/GlobalStateProvider"
import { KeyValueList } from "../Components/KeyValueList"
import { TextWithNewLines } from "../Components/TextWithNewLines"
import { WarningBanner } from "../Pages/AlertBanners"
import { buildRoute, eRoutes } from "../routes"
import { IRecord } from "../types/schema"
import { currencyNameToSymbol, formatPrice } from "../utils/utils"
import { HoverResourceLoader } from "./HoverResourceLoader"
import { HoverSalesRepLoader } from "./HoverSalesRepLoader"
import { sum } from "../utils/arrays"
import { IMemoForDisplay, memoDisplayAttributes, useCustomerMemo } from "../hooks/useCustomerMemo"
import { skeemFetch2CacheFetch } from "../utils/dbCache"

type ICustomer = IRecord<
  "customers",
  | "name"
  | "account_reference"
  | "cash_sales"
  | "credit_limit_pennies"
  | "outstanding_balance_pennies"
  | "tax_rate"
  | "analysisCodes"
  | "currency_name"
  | "memo"
  | "account_on_hold"
  | "sla_level"
  | "webUrl"
  | "current_period_balance"
  | "period_1_balance"
  | "period_2_balance"
  | "period_3_balance"
  | "period_4_balance"
  | "period_5_balance"
  | "period_6_balance"
  | "period_7_balance"
  | "period_8_balance"
  | "status_reason"
  | "payment_terms_days"
  | "payment_terms_basis"
  | "credit_reference"
> & {
  mtd_pennies: number | null
  ytd_pennies: number | null
  last_month_pennies: number | null
  cache_lines_of_business: string[]
  customer_memos: IMemoForDisplay[]
  customer_alerts: IMemoForDisplay[]
  sales_rep: IRecord<"sales_reps", "name"> | null
}

interface IProps {
  id?: string
  initialData?: Partial<ICustomer>
  renderInlineLink?: "block" | "inline"
  inline?: boolean
  includeEventList?: boolean
  startExpanded?: boolean
  noLink?: boolean
  stopPropagation?: boolean
}

export function HoverCustomerLoader({
  id,
  initialData,
  renderInlineLink,
  inline,
  includeEventList,
  startExpanded,
  noLink,
  stopPropagation,
}: IProps) {
  const { currency } = useGlobalState()
  const { customerMemoNode, alertCustomerMemos } = useCustomerMemo()

  async function loadData() {
    const lastMonthStart = startOfMonth(sub(new Date(), { months: 1 }))
    const lastMonthEnd = endOfMonth(sub(new Date(), { months: 1 }))
    const som = startOfMonth(new Date())
    const yom = startOfYear(new Date())

    const customer = await skeemFetch2CacheFetch<ICustomer>(
      // bump the version when the query shape changes so old cached records are not reused
      `customer-hover-loader-v2-${id}`,
      { hours: 1 },
      {
        first: {
          name: "customers",
          attributes: [
            "name",
            "account_reference",
            "cash_sales",
            "credit_limit_pennies",
            "outstanding_balance_pennies",
            "tax_rate",
            "analysisCodes",
            "memo",
            "payment_terms_days",
            "payment_terms_basis",
            "credit_reference",
            {
              name: "customer_memos",
              attributes: memoDisplayAttributes,
              filter: { matches: { display_as_alert: false } },
              sort: { by: "created_at", direction: "desc" },
            },
            {
              as: "customer_alerts",
              value: {
                attr: {
                  name: "customer_memos",
                  attributes: memoDisplayAttributes,
                  filter: { matches: { display_as_alert: true } },
                  sort: { by: "created_at", direction: "desc" },
                },
              },
            },
            "account_on_hold",
            "sla_level",
            { name: "sales_rep", attributes: ["name"] },
            "currency_name",
            "webUrl",
            "cache_lines_of_business",
            "current_period_balance",
            "period_1_balance",
            "period_2_balance",
            "period_3_balance",
            "period_4_balance",
            "period_5_balance",
            "period_6_balance",
            "period_7_balance",
            "period_8_balance",
            "status_reason",
            {
              as: "last_month_pennies",
              value: {
                sumPath: [
                  {
                    name: "invoice_items",
                    filter: {
                      and: [
                        { matches: { invoice_date: { gte: lastMonthStart } } },
                        { matches: { invoice_date: { lte: lastMonthEnd } } },
                      ],
                    },
                  },
                  "cache_revenue",
                ],
              },
            },
            {
              as: "mtd_pennies",
              value: {
                sumPath: [
                  { name: "invoice_items", filter: { matches: { invoice_date: { gte: som } } } },
                  "cache_revenue",
                ],
              },
            },
            {
              as: "ytd_pennies",
              value: {
                sumPath: [
                  { name: "invoice_items", filter: { matches: { invoice_date: { gte: yom } } } },
                  "cache_revenue",
                ],
              },
            },
          ],
          filter: { matches: { id } },
        },
      },
    )
    return customer
  }

  if (!id) {
    return <span>-</span>
  }

  return (
    <React.Fragment>
      {customerMemoNode}
      <HoverResourceLoader
        reloadKey={id}
        loadData={loadData}
        initialData={initialData}
        viewLink={
          noLink
            ? undefined
            : customer => buildRoute(eRoutes["/customers/:id"], { id: customer.id })
        }
        resourceName="Customer"
        renderHoverContentTitle={record => (
          <React.Fragment>
            {record.name}
            {record.account_on_hold && <Spacer />}
            {record.account_on_hold && (
              <P weight={600}>
                This account is on hold:
                {record.status_reason && (
                  <React.Fragment>
                    <br />
                    <b>
                      <TextWithNewLines text={record.status_reason} />
                    </b>
                  </React.Fragment>
                )}
              </P>
            )}
          </React.Fragment>
        )}
        renderInlineLink={renderInlineLink}
        inline={inline}
        color={data => (data?.account_on_hold ? "#fffbd2" : undefined)}
        renderHoverContent={record => (
          <div>
            <KeyValueList
              labelWidth={100}
              data={[
                { label: "Account Ref", value: record.account_reference },
                {
                  label: "Credit Limit",
                  value: record.credit_limit_pennies
                    ? formatPrice(record.credit_limit_pennies, {
                        isPennies: true,
                        currency: currency ?? currencyNameToSymbol(record.currency_name),
                      })
                    : undefined,
                  style:
                    record.credit_limit_pennies &&
                    record.outstanding_balance_pennies &&
                    record.outstanding_balance_pennies > record.credit_limit_pennies
                      ? css`
                          color: ${brandColors.red};
                          font-weight: 600;
                        `
                      : undefined,
                },
                {
                  label: "Outstanding balance",
                  value: record.outstanding_balance_pennies
                    ? formatPrice(record.outstanding_balance_pennies, {
                        isPennies: true,
                        currency: currency ?? currencyNameToSymbol(record.currency_name),
                      })
                    : undefined,
                  style:
                    record.credit_limit_pennies &&
                    record.outstanding_balance_pennies &&
                    record.outstanding_balance_pennies > record.credit_limit_pennies
                      ? css`
                          color: ${brandColors.red};
                          font-weight: 600;
                        `
                      : undefined,
                  tooltip: (
                    <KeyValueList
                      data={[
                        {
                          label: "Current period",
                          value: formatPrice(record.current_period_balance ?? 0, { currency }),
                        },
                        {
                          label: "1 Month",
                          value: formatPrice(record.period_1_balance ?? 0, { currency }),
                        },
                        {
                          label: "2 Months",
                          value: formatPrice(record.period_2_balance ?? 0, { currency }),
                        },
                        {
                          label: "3 Months",
                          value: formatPrice(record.period_3_balance ?? 0, { currency }),
                        },
                        {
                          label: "4 Months +",
                          value: formatPrice(
                            sum([
                              record.period_4_balance ?? 0,
                              record.period_5_balance ?? 0,
                              record.period_6_balance ?? 0,
                              record.period_7_balance ?? 0,
                              record.period_8_balance ?? 0,
                            ]),
                            { currency },
                          ),
                        },
                      ]}
                      light
                    />
                  ),
                },

                {
                  label: "Sales Rep",
                  value: record.sales_rep ? (
                    <HoverSalesRepLoader id={record.sales_rep.id} initialData={record.sales_rep} />
                  ) : undefined,
                },
                ...(
                  record.analysisCodes
                    ?.filter((code: any) => code.value && code.name)
                    .filter((code: any) => code.name !== "Sales Rep") ?? []
                ).map((code: any) => ({
                  label: code.name,
                  value: code.value,
                })),
                {
                  label: "Credit Reference",
                  value: record.credit_reference,
                },
                record.customer_alerts.length > 0 && {
                  label: `Alerts (${record.customer_alerts.length})`,
                  value: (
                    <div onClick={() => alertCustomerMemos(record.id, { onlyOfType: "alerts" })}>
                      <P
                        css={css`
                          text-decoration: 1px dashed underline ${colors.lightBorder};
                          width: max-content;
                          cursor: pointer;
                        `}
                      >
                        View Alerts
                      </P>
                    </div>
                  ),
                },
                record.customer_memos.length > 0 && {
                  label: `Memos (${record.customer_memos.length})`,
                  value: (
                    <div onClick={() => alertCustomerMemos(record.id, { onlyOfType: "memos" })}>
                      <P
                        css={css`
                          text-decoration: 1px dashed underline ${colors.lightBorder};
                          width: max-content;
                          cursor: pointer;
                        `}
                      >
                        View Memos
                      </P>
                    </div>
                  ),
                },
              ]}
              moreData={[
                {
                  label: "Website",
                  value: record.webUrl ? (
                    <ExternalLink to={record.webUrl}>{record.webUrl}</ExternalLink>
                  ) : undefined,
                },
                { label: "Currency", value: record.currency_name },
                { label: "SLA Level", value: titleize(record.sla_level ?? "") },
                { label: "Cash Sales", value: String(!!record.cash_sales) },
                {
                  label: "Spend (Last Month)",
                  value: record.last_month_pennies
                    ? formatPrice(record.last_month_pennies, { currency, isPennies: true })
                    : null,
                },
                {
                  label: "Spend (MTD)",
                  value: record.mtd_pennies
                    ? formatPrice(record.mtd_pennies, { currency, isPennies: true })
                    : null,
                },
                {
                  label: "Spend (YTD)",
                  value: record.ytd_pennies
                    ? formatPrice(record.ytd_pennies, { currency, isPennies: true })
                    : null,
                },
                {
                  label: "Lines of Business",
                  value: (
                    <span
                      css={css`
                        max-width: 200px;
                        display: block;
                      `}
                    >
                      {record.cache_lines_of_business.join(", ")}
                    </span>
                  ),
                },
                {
                  label: "Payment Terms",
                  value: record.payment_terms_days
                    ? `${record.payment_terms_days} days ${
                        record.payment_terms_basis
                          ? humanize(record.payment_terms_basis.replace("PaymentDue", ""))
                          : ""
                      }`
                    : undefined,
                  returnNullOnEmpty: true,
                },
              ]}
              startExpanded={startExpanded}
              inline
            />
            <CreditLimitsStatus record={record} />
            {includeEventList && (
              <React.Fragment>
                <Spacer size={10} />
                <Hr />
                <Spacer size={10} />
                <EventList
                  id={record.id}
                  model="customers"
                  limit={3}
                  viewAllLink={buildRoute(eRoutes["/customers/:id/history"], { id })}
                />
              </React.Fragment>
            )}
          </div>
        )}
        renderTarget={record => (
          <span onClick={stopPropagation ? e => e.stopPropagation() : undefined}>
            {record.name}
          </span>
        )}
      />
    </React.Fragment>
  )
}

export function CreditLimitsStatus({ record }: { record?: ICustomer }) {
  let creditLimitStatus: "" | "at" | "over" = ""
  if (record?.outstanding_balance_pennies) {
    const limit = record.credit_limit_pennies ?? 0
    if (record.outstanding_balance_pennies > limit) {
      creditLimitStatus = "over"
    } else if (record.outstanding_balance_pennies === limit) {
      creditLimitStatus = "at"
    }
  }

  if (creditLimitStatus) {
    return (
      <React.Fragment>
        <Spacer />
        <WarningBanner message={`Customer is ${creditLimitStatus} their credit limit`} compact />
      </React.Fragment>
    )
  }
  return null
}
